/**
 * Visits module strings (`visitsPage.*`), merged into the dictionary through `extra.ts`.
 * `visitsEn` is typed against `visitsPl`, so a missing key is a compile error.
 */
import { visitsViewsEn, visitsViewsPl } from './visits-views';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const visitsPl = {
  ...visitsViewsPl,
  subtitle: 'Historia i bieżący przebieg wizyt handlowych — kto, u kogo i z jakim wynikiem.',
  loadError: 'Nie udało się wczytać wizyt',
  searchPlaceholder: 'Szukaj po nazwie klienta…',
  allReps: 'Wszyscy handlowcy',
  rep: 'Handlowiec',
  periodLabel: 'Okres',
  period: {
    today: 'Dziś',
    yesterday: 'Wczoraj',
    d7: '7 dni',
    d30: '30 dni',
    d90: '90 dni',
    all: 'Cały okres',
  },
  tabs: {
    all: 'Wszystkie',
    IN_PROGRESS: 'W trakcie',
    COMPLETED: 'Zakończone',
    PLANNED: 'Zaplanowane',
    CANCELLED: 'Anulowane',
    MISSED: 'Nieodbyte',
  },
  status: {
    PLANNED: 'Zaplanowana',
    IN_PROGRESS: 'W trakcie',
    COMPLETED: 'Zakończona',
    CANCELLED: 'Anulowana',
    MISSED: 'Nieodbyta',
  },
  filters: {
    status: 'Status',
    rep: 'Handlowiec',
    city: 'Miasto',
    source: 'Źródło',
    customer: 'Klient',
    planned: 'Termin',
    duration: 'Czas (min)',
  },
  source: {
    GPS: 'Z GPS',
    MANUAL: 'Ręcznie',
  },
  empty: {
    title: 'Brak wizyt',
    body: 'Zmień okres lub filtry. Wizyty pojawią się tu, gdy handlowcy zaczną je rejestrować w aplikacji mobilnej.',
  },
  row: {
    steps: '{done}/{total} kroków',
    signed: 'Podpis klienta',
    duration: '{min} min',
    planned: 'Plan: {date}',
    noDate: 'Bez terminu',
  },
  stats: {
    total: 'Wizyty w okresie',
    totalHint: 'Wszystkie statusy',
    completed: 'Zakończone',
    completedHint: '{pct} wszystkich wizyt',
    completedNone: 'Brak zakończonych wizyt',
    inProgress: 'W trakcie',
    inProgressHint: 'Aktualnie u klientów',
    inProgressNone: 'Nikt nie jest teraz u klienta',
    avg: 'Średni czas wizyty',
    avgHint: 'Zakończone wizyty',
    avgNone: 'Brak danych',
    avgValue: '{min} min',
  },
  insight: {
    inProgress: 'Teraz trwa wizyt w terenie: {count}.',
    showInProgress: 'Pokaż trwające',
    missed: 'Wizyt, które się nie odbyły w tym okresie: {count}.',
    showMissed: 'Pokaż nieodbyte',
    clear: 'Wizyty idą zgodnie z planem.',
  },
  detail: {
    title: 'Wizyta',
    close: 'Zamknij',
    loadError: 'Nie udało się wczytać szczegółów wizyty.',
    customer: 'Klient',
    openCustomer: 'Karta klienta',
    when: 'Termin',
    started: 'Początek',
    ended: 'Koniec',
    duration: 'Czas trwania',
    rep: 'Handlowiec',
    source: 'Źródło',
    notes: 'Notatka',
    noNotes: 'Brak notatki.',
    steps: 'Przebieg wizyty',
    stepStatus: {
      PENDING: 'Do zrobienia',
      DONE: 'Zrobione',
      SKIPPED: 'Pominięte',
    },
    mandatory: 'obowiązkowy',
    survey: 'Ankieta',
    noSurvey: 'Ankieta nie została wypełniona.',
    answerYes: 'Tak',
    answerNo: 'Nie',
    noAnswer: '—',
    consents: 'Zgody',
    noConsents: 'Brak zarejestrowanych zgód.',
    consent: {
      RODO: 'Klauzula RODO',
      MARKETING: 'Zgoda marketingowa',
      granted: 'Udzielona',
      denied: 'Odmowa',
    },
    signature: 'Podpis klienta',
    noSignature: 'Brak podpisu.',
    signedBy: '{name}, {date}',
  },
} as const;

export const visitsEn: Shape<typeof visitsPl> = {
  ...visitsViewsEn,
  subtitle: 'History and live progress of sales visits — who, where and with what result.',
  loadError: 'Could not load visits',
  searchPlaceholder: 'Search by customer name…',
  allReps: 'All reps',
  rep: 'Rep',
  periodLabel: 'Period',
  period: {
    today: 'Today',
    yesterday: 'Yesterday',
    d7: '7 days',
    d30: '30 days',
    d90: '90 days',
    all: 'All time',
  },
  tabs: {
    all: 'All',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    PLANNED: 'Planned',
    CANCELLED: 'Cancelled',
    MISSED: 'Missed',
  },
  status: {
    PLANNED: 'Planned',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    MISSED: 'Missed',
  },
  filters: {
    status: 'Status',
    rep: 'Rep',
    city: 'City',
    source: 'Source',
    customer: 'Customer',
    planned: 'Planned',
    duration: 'Duration (min)',
  },
  source: {
    GPS: 'From GPS',
    MANUAL: 'Manual',
  },
  empty: {
    title: 'No visits',
    body: 'Change the period or filters. Visits show up here once reps start logging them in the mobile app.',
  },
  row: {
    steps: '{done}/{total} steps',
    signed: 'Customer signature',
    duration: '{min} min',
    planned: 'Planned: {date}',
    noDate: 'No date',
  },
  stats: {
    total: 'Visits in period',
    totalHint: 'All statuses',
    completed: 'Completed',
    completedHint: '{pct} of all visits',
    completedNone: 'No completed visits',
    inProgress: 'In progress',
    inProgressHint: 'Currently at customers',
    inProgressNone: 'Nobody is at a customer right now',
    avg: 'Average visit time',
    avgHint: 'Completed visits',
    avgNone: 'No data',
    avgValue: '{min} min',
  },
  insight: {
    inProgress: 'Visits in progress in the field right now: {count}.',
    showInProgress: 'Show in progress',
    missed: 'Visits that did not take place in this period: {count}.',
    showMissed: 'Show missed',
    clear: 'Visits are on track.',
  },
  detail: {
    title: 'Visit',
    close: 'Close',
    loadError: 'Could not load visit details.',
    customer: 'Customer',
    openCustomer: 'Customer card',
    when: 'Date',
    started: 'Started',
    ended: 'Ended',
    duration: 'Duration',
    rep: 'Rep',
    source: 'Source',
    notes: 'Note',
    noNotes: 'No note.',
    steps: 'Visit progress',
    stepStatus: {
      PENDING: 'To do',
      DONE: 'Done',
      SKIPPED: 'Skipped',
    },
    mandatory: 'mandatory',
    survey: 'Survey',
    noSurvey: 'The survey was not filled in.',
    answerYes: 'Yes',
    answerNo: 'No',
    noAnswer: '—',
    consents: 'Consents',
    noConsents: 'No consents recorded.',
    consent: {
      RODO: 'GDPR notice',
      MARKETING: 'Marketing consent',
      granted: 'Granted',
      denied: 'Declined',
    },
    signature: 'Customer signature',
    noSignature: 'No signature.',
    signedBy: '{name}, {date}',
  },
};
